import { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * FE-BU03: the two ways out of a page holding work the server does not have.
 *
 * This covers unsnapshotted uploads and Review corrections awaiting completion.
 * Closing the tab or taking a sidebar link must warn while either exists only
 * in component state. Callers can also retain the guard during an active submit.
 */
export const UNSAVED_WORK_WARNING =
  'Your upload has unsaved changes. Leaving now may lose them.';

const warnOnUnload = (event) => {
  event.preventDefault();
  // Browsers show their own wording; a non-empty value is what triggers it.
  event.returnValue = UNSAVED_WORK_WARNING;
  return UNSAVED_WORK_WARNING;
};

const beforeUnloadGuard = (active) => {
  if (!active) return undefined;
  window.addEventListener('beforeunload', warnOnUnload);
  return () => window.removeEventListener('beforeunload', warnOnUnload);
};

// history is undefined outside a Router, which component tests generally are.
const routeGuard = (history, active) => {
  if (!active || !history?.block) return undefined;
  // history.block() returns an unblock function that must be called to remove the guard
  const unblock = history.block(UNSAVED_WORK_WARNING);
  return unblock;
};

export const useUnsavedWorkGuard = (hasUnsavedWork) => {
  const history = useHistory();
  const cleanupRef = useRef(null);
  const release = useCallback(() => cleanupRef.current?.(), []);
  useEffect(() => {
    const removeUnload = beforeUnloadGuard(hasUnsavedWork);
    const unblock = routeGuard(history, hasUnsavedWork);
    let released = false;
    const cleanup = () => {
      if (released) return;
      released = true;
      removeUnload?.();
      unblock?.();
    };
    cleanupRef.current = cleanup;
    return cleanup;
  }, [history, hasUnsavedWork]);
  // Successful saves may navigate in the same callback, before effect cleanup.
  return release;
};
